// LibreTranslate Service for BeeVids
// Works with local Docker installation or public instances

class LibreTranslateService {
    constructor(apiUrl = 'http://localhost:5000') {
        this.apiUrl = apiUrl;
        this.defaultInstances = [
            'http://localhost:5000',           // Local Docker
            'https://libretranslate.de',       // German public instance (more reliable)
            'https://translate.terraprint.co', // Alternative reliable instance
            'https://libretranslate.com',      // Official public (has limits)
            'https://translate.argosopentech.com'  // Alternative public
        ];
    }

  // Get supported languages
  async getSupportedLanguages() {
    try {
      const response = await fetch(`${this.apiUrl}/languages`);
      
      if (!response.ok) {
        throw new Error(`LibreTranslate API error: ${response.status}`);
      }

      const languages = await response.json();
      return languages.map(lang => ({
        code: lang.code,
        name: lang.name
      }));
    } catch (error) {
      console.error('Error fetching LibreTranslate languages:', error);
      // Return fallback languages
      return [
        { code: 'ar', name: 'Arabic' },
        { code: 'zh', name: 'Chinese' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'hi', name: 'Hindi' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'ko', name: 'Korean' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'es', name: 'Spanish' },
        { code: 'tr', name: 'Turkish' }
      ];
    }
  }

  // Translate a single text
  async translateText(text, targetLanguage, sourceLanguage = 'auto') {
    try {
      // Validate and normalize language codes
      const normalizedTarget = this.normalizeLanguageCode(targetLanguage);
      const normalizedSource = sourceLanguage === 'auto' ? 'auto' : this.normalizeLanguageCode(sourceLanguage);
      
      const requestBody = {
        q: text.trim(),
        source: normalizedSource,
        target: normalizedTarget,
        format: 'text'
      };

      console.log('LibreTranslate request:', requestBody);

      const response = await fetch(`${this.apiUrl}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const responseText = await response.text();
      console.log('LibreTranslate response:', response.status, responseText);

      if (!response.ok) {
        let errorMessage = `LibreTranslate API error: ${response.status}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage += ` - ${responseText}`;
        }
        throw new Error(errorMessage);
      }

      const result = JSON.parse(responseText);
      
      if (result.translatedText && result.translatedText.trim() !== text.trim()) {
        return {
          success: true,
          translatedText: result.translatedText,
          detectedLanguage: normalizedSource,
          confidence: 0.80
        };
      } else {
        throw new Error('Translation returned same text or empty result');
      }
    } catch (error) {
      console.error('LibreTranslate error:', error);
      return {
        success: false,
        error: error.message,
        translatedText: text // Return original text on failure
      };
    }
  }

  // Normalize language codes for LibreTranslate
  normalizeLanguageCode(code) {
    const languageMap = {
      'ar': 'ar',
      'zh': 'zh',
      'en': 'en', 
      'fr': 'fr',
      'de': 'de',
      'hi': 'hi',
      'it': 'it',
      'ja': 'ja',
      'ko': 'ko',
      'pt': 'pt',
      'ru': 'ru',
      'es': 'es',
      'tr': 'tr'
    };
    
    return languageMap[code.toLowerCase()] || code.toLowerCase();
  }

  // Translate multiple segments with progress tracking
  async translateSegments(segments, targetLanguage, sourceLanguage = 'auto', onProgress) {
    const results = [];
    const batchSize = 2; // Small batches for local service
    
    for (let i = 0; i < segments.length; i += batchSize) {
      const batch = segments.slice(i, i + batchSize);
      const batchPromises = batch.map(async (segment) => {
        // Add delay to be respectful to local service
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const result = await this.translateText(segment.text, targetLanguage, sourceLanguage);
        
        return {
          ...segment,
          text: result.translatedText,
          originalText: segment.text,
          translationLanguage: targetLanguage,
          detectedLanguage: result.detectedLanguage,
          translationConfidence: result.confidence || 0.80,
          translationError: result.success ? null : result.error
        };
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Report progress
      const progress = Math.round((results.length / segments.length) * 100);
      if (onProgress) {
        onProgress(progress, `Translated ${results.length}/${segments.length} segments`);
      }

      // Delay between batches
      if (i + batchSize < segments.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    return {
      success: true,
      translatedSegments: results,
      totalSegments: segments.length,
      targetLanguage,
      sourceLanguage
    };
  }

  // Test if LibreTranslate is available
  async testConnection() {
    try {
      const response = await fetch(`${this.apiUrl}/languages`);
      return {
        success: response.ok,
        message: response.ok ? 'LibreTranslate is working!' : `LibreTranslate returned ${response.status}`
      };
    } catch (error) {
      return {
        success: false,
        message: `LibreTranslate test failed: ${error.message}`
      };
    }
  }

  // Check if LibreTranslate is set up locally
  async checkLocalSetup() {
    try {
      // Check if local Docker instance is running
      const localResponse = await fetch('http://localhost:5000/languages', {
        method: 'GET',
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      
      if (localResponse.ok) {
        return {
          hasLocal: true,
          url: 'http://localhost:5000',
          message: 'Local LibreTranslate Docker instance detected'
        };
      }
    } catch (error) {
      // Local instance not found
    }

    // Try public instances
    for (const instance of this.defaultInstances.slice(1)) {
      try {
        const response = await fetch(`${instance}/languages`, {
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        });
        
        if (response.ok) {
          return {
            hasLocal: false,
            url: instance,
            message: `Using public instance: ${instance}`,
            warning: 'Public instances may have rate limits'
          };
        }
      } catch (error) {
        continue;
      }
    }

    return {
      hasLocal: false,
      url: null,
      message: 'No LibreTranslate instance found',
      error: 'Please set up LibreTranslate locally or check internet connection'
    };
  }

  // Get setup instructions
  getSetupInstructions() {
    return {
      title: 'LibreTranslate Setup',
      description: 'Free, open-source translation service that runs locally',
      options: [
        {
          method: 'Docker (Recommended)',
          steps: [
            '1. Install Docker Desktop from docker.com',
            '2. Open terminal/command prompt',
            '3. Run: docker run -ti --rm -p 5000:5000 libretranslate/libretranslate',
            '4. Wait for "Running on http://0.0.0.0:5000"',
            '5. Try translation again'
          ],
          requirements: ['Docker Desktop', '~2GB RAM', '~500MB download']
        },
        {
          method: 'Python Installation',
          steps: [
            '1. Install Python 3.8+ from python.org',
            '2. Run: pip install libretranslate',
            '3. Run: libretranslate --host 0.0.0.0 --port 5000',
            '4. Try translation again'
          ],
          requirements: ['Python 3.8+', '~1GB disk space', 'pip package manager']
        },
        {
          method: 'Use Public Instance',
          steps: [
            '1. No setup required',
            '2. Uses public LibreTranslate servers',
            '3. May have rate limits and slower performance'
          ],
          requirements: ['Internet connection'],
          warning: 'Not recommended for large amounts of text'
        }
      ]
    };
  }

  // Set API URL for different instances
  setApiUrl(url) {
    this.apiUrl = url;
  }
}

export default LibreTranslateService; 